import React, { useRef, useState } from "react";
import { Button, Flex, ModalV2, Textarea, Text, toast } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { SPACING } from "#utils/spacing";
import * as styles from "../styles_vanilla.css";

type UploadStatus =
  | { kind: "idle" }
  | { kind: "uploading" }
  | { kind: "staged"; documentId: number; fileName: string }
  | { kind: "error"; message: string };

interface ConfirmOnBehalfModalProps {
  isOpen: boolean;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (note: string) => void;
  stageDocument?: (file: File) => Promise<number>;
  deleteUploadedDocument?: (documentId: number) => Promise<void>;
}

// Confirmation modal for the fund admin recording an external approval on the
// client's (GP's) behalf. The BE requires a non-null note as the audit record
// of the off-platform decision, so the confirm button stays disabled until a
// note is entered. Optionally supports uploading an evidence document that gets
// attached to the task via the existing flushPendingAttachments flow on submit.
export const ConfirmOnBehalfModal: React.FC<ConfirmOnBehalfModalProps> = ({
  isOpen,
  loading = false,
  onCancel,
  onConfirm,
  stageDocument,
  deleteUploadedDocument,
}) => {
  const [note, setNote] = useState("");
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({ kind: "idle" });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const cleanupStagedDocument = () => {
    if (uploadStatus.kind === "staged" && deleteUploadedDocument) {
      deleteUploadedDocument(uploadStatus.documentId).catch(() =>
        toast.error("Could not remove the staged document."),
      );
    }
    setUploadStatus({ kind: "idle" });
  };

  const handleHide = () => {
    setNote("");
    cleanupStagedDocument();
    onCancel();
  };

  const handleSubmit = () => {
    onConfirm(note);
    setNote("");
    setUploadStatus({ kind: "idle" });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !stageDocument) return;

    if (uploadStatus.kind === "staged" && deleteUploadedDocument) {
      await deleteUploadedDocument(uploadStatus.documentId);
    }

    setUploadStatus({ kind: "uploading" });
    try {
      const documentId = await stageDocument(file);
      setUploadStatus({ kind: "staged", documentId, fileName: file.name });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed.";
      setUploadStatus({ kind: "error", message });
      toast.error(message);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteStaged = async () => {
    if (uploadStatus.kind !== "staged" || !deleteUploadedDocument) return;
    try {
      await deleteUploadedDocument(uploadStatus.documentId);
      setUploadStatus({ kind: "idle" });
    } catch {
      toast.error("Failed to remove the document. Please try again.");
    }
  };

  const confirmDisabled = loading || !note.trim();

  return (
    <ModalV2
      show={isOpen}
      onHide={handleHide}
      title="Approve on Client's Behalf"
      size="medium"
      data-testid="confirm-on-behalf-modal"
    >
      <ModalV2.Body>
        <Flex flexDirection="column" gap={SPACING.LARGE}>
          <Text variant="regular-md">
            Record this approval on the client&apos;s behalf. Add a note documenting the
            client&apos;s off-platform decision — this is stored as audit evidence on the
            approval.
          </Text>
          <div>
            <Textarea
              name="confirm-on-behalf-note"
              label="Note"
              value={note}
              onSetValue={setNote}
              placeholder="Document the client's decision and any supporting context"
              isFullWidth
              size="medium"
            />
            <div className={styles.modalNoteHint}>Required</div>
          </div>

          {stageDocument && (
            <div data-testid="on-behalf-upload-section">
              <div className={styles.modalLabel}>Evidence Document (Optional)</div>

              {/* Hidden file input — no DS file-picker exists */}
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                onChange={(e) => {
                  void handleFileChange(e);
                }}
                data-testid="on-behalf-upload-input"
              />

              {uploadStatus.kind !== "staged" && (
                <Flex
                  alignItems="center"
                  gap={Sizes.SM}
                  className={styles.modalUploadZone}
                  data-testid="on-behalf-upload-zone"
                >
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading || uploadStatus.kind === "uploading"}
                    variant="neutral"
                    data-testid="on-behalf-upload-button"
                  >
                    {uploadStatus.kind === "uploading" ? "Uploading…" : "Upload File"}
                  </Button>
                </Flex>
              )}

              {uploadStatus.kind === "staged" && (
                <Flex
                  alignItems="center"
                  gap={Sizes.SM}
                  className={styles.modalUploadCard}
                  data-testid="on-behalf-upload-card"
                >
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    className={styles.modalUploadIconBox}
                  >
                    <Icon
                      type="file-alt"
                      color={Colors.Neutral600}
                      size="medium"
                      variant="solid"
                    />
                  </Flex>
                  <Text
                    className={styles.modalUploadFileName}
                    data-testid="on-behalf-upload-filename"
                  >
                    {uploadStatus.fileName}
                  </Text>
                  <Button
                    onClick={() => {
                      void handleDeleteStaged();
                    }}
                    aria-label="Remove file"
                    data-testid="on-behalf-upload-delete"
                    icon="trash"
                    variant="neutral"
                  />
                </Flex>
              )}

              {uploadStatus.kind === "error" && (
                <Text variant="secondary-sm" color={Colors.Danger600}>
                  {uploadStatus.message}
                </Text>
              )}
            </div>
          )}
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end" gap={SPACING.SMALL} className={styles.modalFooter}>
          <button
            className={styles.actionButton}
            onClick={handleHide}
            disabled={loading}
            data-testid="confirm-on-behalf-cancel"
          >
            Cancel
          </button>
          <button
            className={styles.actionButtonPrimary}
            onClick={handleSubmit}
            disabled={confirmDisabled}
            data-testid="confirm-on-behalf-confirm"
          >
            Approve on Client&apos;s Behalf
          </button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};

export default ConfirmOnBehalfModal;
